import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlass as Search,
  UploadSimple as Upload,
  FilePdf,
  FileDoc,
  FileText,
  DownloadSimple as Download,
  Spinner,
  ListBullets,
  SquaresFour,
  FunnelSimple as Filter,
  X,
  Archive as ArchiveIcon,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RowActions } from '@/components/shared/RowActions';
import { UploadDocumentModal } from '@/components/shared/UploadDocumentModal';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import { EditDocumentModal } from '@/components/shared/EditDocumentModal';
import { fetchDocuments, downloadDocument, deleteDocument, archiveDocument, unarchiveDocument, type DocFile } from '@/lib/documentsApi';
import { useDocumentsStore } from '@/lib/documentsStore';
import { useDebounce } from '@/lib/useDebounce';
import { useAuthStore } from '@/features/auth/store';
import { canDeleteDocument } from '@/lib/pupilUiBoundary';

function FileIcon({ kind }: { kind: string }) {
  if (kind === 'pdf') return <FilePdf className="h-5 w-5 text-red-600 dark:text-red-400" weight="fill" />;
  if (kind === 'doc') return <FileDoc className="h-5 w-5 text-blue-600 dark:text-blue-400" weight="fill" />;
  return <FileText className="h-5 w-5 text-muted-foreground" />;
}

const DOC_TYPES = ['brief', 'skeleton', 'authorities', 'bundle', 'opinion', 'evidence', 'correspondence', 'other'] as const;

export default function MyDocumentsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [fileSearch, setFileSearch] = useState('');
  const debouncedSearch = useDebounce(fileSearch.trim(), 300);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [editDoc, setEditDoc] = useState<DocFile | null>(null);
  /**
   * Archived view. ARCHIVE IS NOT DELETE — an archived document keeps its file, versions and audit
   * trail and comes back with Restore, so it needs somewhere to be seen rather than vanishing.
   */
  const [showArchived, setShowArchived] = useState(false);
  /**
   * The RBAC key itself, not a pupil predicate. `document.archive` is held by pupil AND the senior
   * document roles (chambers_admin, senior_clerk, silk) but NOT by junior barristers, junior clerks
   * or staff — so a pupil-shaped predicate would be the wrong shape and would mis-answer for three
   * roles. Same convention as `contact.delete` on the Clients page.
   */
  const canArchive = useAuthStore((s) => s.permissions).includes('document.archive');

  const storeFiles = useDocumentsStore((s) => s.files);
  const storeFetch = useDocumentsStore((s) => s.fetchDocuments);
  const storeLoading = useDocumentsStore((s) => s.loading);
  const storeError = useDocumentsStore((s) => s.error);

  // Initial fetch
  useEffect(() => {
    storeFetch();
  }, [storeFetch]);

  // Search query
  const searchQ = useQuery({
    queryKey: ['documents-search', debouncedSearch],
    queryFn: () => fetchDocuments(undefined, debouncedSearch),
    enabled: debouncedSearch.length >= 2,
    staleTime: 30_000,
  });

  // The store holds the ACTIVE list (the API excludes archived by default). The archived view is
  // its own query against ?archived=true, so the two never mix.
  const archivedQ = useQuery({
    queryKey: ['documents-archived'],
    queryFn: () => fetchDocuments(undefined, undefined, true),
    enabled: showArchived,
    staleTime: 30_000,
  });

  const rawFiles = showArchived
    ? (archivedQ.data ?? [])
    : debouncedSearch.length >= 2 ? (searchQ.data ?? []) : storeFiles;
  const files = useMemo(() => {
    if (!filterType) return rawFiles;
    // Filter on the raw category enum (what DOC_TYPES holds) — NOT `kind`, which is the
    // display label (e.g. 'skeleton' → 'Skeleton argument'), so matching it never worked.
    return rawFiles.filter((f) => f.category.toLowerCase() === filterType.toLowerCase());
  }, [rawFiles, filterType]);
  const isLoading = showArchived
    ? archivedQ.isLoading
    : storeLoading || (debouncedSearch.length >= 2 && searchQ.isLoading);

  /** Refresh whichever list is on screen, plus the other one, after an archive/restore. */
  const refreshBothLists = () => { storeFetch(); archivedQ.refetch(); };

  const handleArchiveToggle = async (f: DocFile) => {
    const archived = !!f.archivedAt;
    try {
      if (archived) {
        await unarchiveDocument(f.id);
        toast.success(`${f.name} restored`);
      } else {
        await archiveDocument(f.id);
        toast.success(`${f.name} archived`);
      }
      refreshBothLists();
    } catch {
      toast.error(archived ? 'Could not restore document — please try again' : 'Could not archive document — please try again');
    }
  };

  const handleDownload = async (file: DocFile) => {
    try {
      // Download via the canonical authenticated stream endpoint (no presigned storage
      // URL exposed; auth + matter access enforced server-side on every byte).
      await downloadDocument(file.id, file.name);
    } catch {
      toast.error('Could not download this document — please try again.');
    }
  };

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="My Documents"
        subtitle="Briefs, opinions, drafts and case papers you've authored or own"
        actions={
          <Button variant="primary" size="md" leftIcon={<Upload className="h-4 w-4" />} onClick={() => setUploadOpen(true)}>
            Upload document
          </Button>
        }
      />

      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search my documents…"
              className="h-9 flex-1 bg-transparent text-sm focus:outline-none"
              value={fileSearch}
              onChange={(e) => setFileSearch(e.target.value)}
            />
            {searchQ.isFetching && (
              <Spinner className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          <Button
            variant={filterType ? 'primary' : 'outline'}
            size="md"
            leftIcon={<Filter className="h-4 w-4" />}
            onClick={() => setFilterOpen(!filterOpen)}
          >
            {filterType ? `Type: ${filterType}` : 'Filters'}
          </Button>
          {/* Archived documents are not gone — this is where they are. Shown to anyone who holds
              `document.archive`, i.e. anyone who could have put something here. */}
          {canArchive && (
            <Button
              variant={showArchived ? 'primary' : 'outline'}
              size="md"
              leftIcon={<ArchiveIcon className="h-4 w-4" />}
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? 'Viewing archived' : 'Archived'}
            </Button>
          )}
          {filterType && (
            <button
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setFilterType(null)}
              title="Clear filter"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <div className="flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5">
            <button
              onClick={() => setView('list')}
              className={`rounded-sm p-1.5 ${view === 'list' ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <ListBullets className="h-4 w-4" />
            </button>
            <button
              onClick={() => setView('grid')}
              className={`rounded-sm p-1.5 ${view === 'grid' ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <SquaresFour className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Card className="flex items-center justify-center py-16">
          <Spinner className="h-8 w-8 animate-spin text-muted-foreground" />
        </Card>
      ) : (debouncedSearch.length >= 2 ? searchQ.isError : !!storeError) ? (
        // A failed load is not an empty library — never tell the user to "upload your first document".
        <Card className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">Documents could not be loaded</p>
          <p className="mt-1 text-xs text-muted-foreground">Your documents are safe — this is a connection problem.</p>
          <Button className="mt-4" variant="outline" onClick={() => (debouncedSearch.length >= 2 ? searchQ.refetch() : storeFetch())}>
            Try again
          </Button>
        </Card>
      ) : files.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No documents found</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {debouncedSearch ? `No results for "${debouncedSearch}"` : 'Upload your first document to get started'}
          </p>
        </Card>
      ) : view === 'list' ? (
        <Card>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-10 whitespace-nowrap">Actions</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Matter</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id} className="cursor-pointer" onClick={() => setPreviewDocId(f.id)}>
                  <td><FileIcon kind={f.icon} /></td>
                  <td className="font-medium text-foreground">{f.name}</td>
                  <td><Badge>{f.kind}</Badge></td>
                  <td>
                    {f.caseId ? (
                      <button
                        className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          // WORK-2: navigate by the matter UUID, not the case number (which 404'd).
                          navigate(`/barrister/matters/${f.caseId}`);
                        }}
                      >
                        {f.case}
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="text-xs text-muted-foreground">{f.size}</td>
                  <td className="text-xs text-muted-foreground">{f.uploaded}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                        title="Download"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(f);
                        }}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      {/* Archive / Restore — one control, two directions, gated on the RBAC key.
                          Rendered inline rather than inside RowActions because RowActions has no
                          extra-action slot and widening a shared component would touch every list.
                          DELETE stays exactly where it was: a separate, destructive action behind
                          a different permission. Archive is never offered as a substitute for it. */}
                      {canArchive && (
                        <button
                          type="button"
                          title={f.archivedAt ? 'Restore to active documents' : 'Archive (reversible)'}
                          aria-label={f.archivedAt ? 'Restore document' : 'Archive document'}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                          onClick={(e) => { e.stopPropagation(); void handleArchiveToggle(f); }}
                        >
                          {f.archivedAt
                            ? <ArrowCounterClockwise className="h-4 w-4" />
                            : <ArchiveIcon className="h-4 w-4" />}
                        </button>
                      )}
                      <RowActions
                        onView={() => setPreviewDocId(f.id)}
                        onEdit={() => setEditDoc(f)}
                        // Pupils hold document create + update but NOT `document.delete`, so the
                        // row's Delete action is omitted entirely for them (RowActions draws it
                        // only when onDelete is supplied) rather than offered and then 403'd.
                        onDelete={!canDeleteDocument(user?.memberType) ? undefined : async () => {
                          try {
                            await deleteDocument(f.id);
                            toast.success(`${f.name} deleted`);
                            storeFetch();
                          } catch {
                            toast.error('Could not delete document — please try again');
                          }
                        }}
                        editLabel="Edit"
                        deleteConfirm={{
                          title: 'Delete document?',
                          message: `Delete “${f.name}”? This cannot be undone.`,
                          confirmLabel: 'Delete',
                        }}
                      />
                    </div>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {files.map((f) => (
            <Card
              key={f.id}
              className="cursor-pointer p-4 transition hover:border-brand-300 hover:shadow-pop"
              onClick={() => setPreviewDocId(f.id)}
            >
              <div className="mb-3 flex h-28 items-center justify-center rounded-md bg-muted/40">
                <FileIcon kind={f.icon} />
              </div>
              <div className="truncate text-sm font-medium text-foreground">{f.name}</div>
              <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                <span>{f.size}</span>
                <Badge>{f.kind}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      <UploadDocumentModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={() => {
          setUploadOpen(false);
          storeFetch();
        }}
      />

      <DocumentPreviewModal
        documentId={previewDocId}
        onClose={() => setPreviewDocId(null)}
      />

      {/* Filter panel */}
      {filterOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setFilterOpen(false)}>
          <div
            className="absolute right-4 top-48 w-56 rounded-md border border-border bg-card p-3 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Document type
            </div>
            <div className="space-y-1">
              {DOC_TYPES.map((t) => (
                <button
                  key={t}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm capitalize ${
                    filterType === t ? 'bg-brand-50 text-brand-700 dark:text-brand-300' : 'hover:bg-muted'
                  }`}
                  onClick={() => {
                    setFilterType(filterType === t ? null : t);
                    setFilterOpen(false);
                  }}
                >
                  <FileText className="h-3.5 w-3.5" />
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit document — rename + replace file (new version) */}
      {editDoc && (
        <EditDocumentModal
          document={editDoc}
          onClose={() => setEditDoc(null)}
          onSaved={() => storeFetch()}
        />
      )}
    </div>
  );
}
